import { flow, identity } from 'fp-ts-esm/function';
import * as O from 'fp-ts-esm/Option';
import { pipe } from 'fp-ts-esm/pipeable';
import * as _ from './internal.js';
// -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------
/**
 * @category constructors
 * @since 2.3.8
 */
export const prism = _.prism;
/**
 * @category constructors
 * @since 2.3.0
 */
export const id = () => prism(O.some, identity);
/**
 * @category constructors
 * @since 2.3.0
 */
export const fromPredicate = _.prismFromPredicate;
// -------------------------------------------------------------------------------------
// converters
// -------------------------------------------------------------------------------------
/**
 * View a `Prism` as a `Optional`.
 *
 * @category converters
 * @since 2.3.0
 */
export const asOptional = _.prismAsOptional;
/**
 * View a `Prism` as a `Traversal`.
 *
 * @category converters
 * @since 2.3.0
 */
export const asTraversal = _.prismAsTraversal;
// -------------------------------------------------------------------------------------
// compositions
// -------------------------------------------------------------------------------------
/**
 * Compose a `Prism` with a `Prism`.
 *
 * @category compositions
 * @since 2.3.0
 */
export const compose = _.prismComposePrism;
/**
 * Alias of `compose`.
 *
 * @category compositions
 * @since 2.3.8
 */
export const composePrism = compose;
/**
 * Compose a `Prism` with a `Iso`.
 *
 * @category compositions
 * @since 2.3.8
 */
export const composeIso = 
/*#__PURE__*/
flow(_.isoAsPrism, compose);
/**
 * Compose a `Prism` with a `Lens`.
 *
 * @category compositions
 * @since 2.3.0
 */
export const composeLens = _.prismComposeLens;
/**
 * Compose a `Prism` with an `Optional`.
 *
 * @category compositions
 * @since 2.3.0
 */
export const composeOptional = (ab) => flow(asOptional, _.optionalComposeOptional(ab));
/**
 * Compose a `Prism` with an `Traversal`.
 *
 * @category compositions
 * @since 2.3.8
 */
export const composeTraversal = (ab) => flow(asTraversal, _.traversalComposeTraversal(ab));
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
/**
 * @category combinators
 * @since 2.3.0
 */
export const set = _.prismSet;
/**
 * @category combinators
 * @since 2.3.0
 */
export const modifyOption = _.prismModifyOption;
/**
 * @category combinators
 * @since 2.3.0
 */
export const modify = _.prismModify;
export function modifyF(F) {
    return (f) => (sa) => (s) => pipe(sa.getOption(s), O.fold(() => F.of(s), (a) => F.map(f(a), sa.reverseGet)));
}
/**
 * Return a `Prism` from a `Prism` focused on a nullable value.
 *
 * @category combinators
 * @since 2.3.3
 */
export const fromNullable = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismFromNullable());
export function filter(predicate) {
    return compose(_.prismFromPredicate(predicate));
}
/**
 * Return a `Optional` from a `Prism` and a prop.
 *
 * @category combinators
 * @since 2.3.0
 */
export const prop = (prop) => composeLens(pipe(_.lensId(), _.lensProp(prop))); // TODO: simplify?
/**
 * Return a `Optional` from a `Prism` and a list of props.
 *
 * @category combinators
 * @since 2.3.0
 */
export const props = (...props) => composeLens(pipe(_.lensId(), _.lensProps(...props)));
/**
 * Return a `Optional` from a `Prism` focused on a component of a tuple.
 *
 * @category combinators
 * @since 2.3.0
 */
export const component = (prop) => composeLens(pipe(_.lensId(), _.lensComponent(prop)));
/**
 * Return a `Optional` from a `Prism` focused on an index of a `ReadonlyArray`.
 *
 * @category combinators
 * @since 2.3.0
 */
export const index = (i) => flow(asOptional, _.optionalIndex(i));
/**
 * Return a `Optional` from a `Prism` focused on an index of a `ReadonlyNonEmptyArray`.
 *
 * @category combinators
 * @since 2.3.8
 */
export const indexNonEmpty = (i) => flow(asOptional, _.optionalIndexNonEmpty(i));
/**
 * Return a `Optional` from a `Prism` focused on a key of a `ReadonlyRecord`.
 *
 * @category combinators
 * @since 2.3.0
 */
export const key = (key) => flow(asOptional, _.optionalKey(key));
/**
 * Return a `Optional` from a `Prism` focused on a required key of a `ReadonlyRecord`.
 *
 * @category combinators
 * @since 2.3.0
 */
export const atKey = (key) => (sa) => _.prismComposeLens(_.atReadonlyRecord().at(key))(sa);
/**
 * Return a `Prism` from a `Prism` focused on the `Some` of a `Option` type.
 *
 * @category combinators
 * @since 2.3.0
 */
export const some = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismSome());
/**
 * Return a `Prism` from a `Prism` focused on the `Right` of a `Either` type.
 *
 * @category combinators
 * @since 2.3.0
 */
export const right = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismRight());
/**
 * Return a `Prism` from a `Prism` focused on the `Left` of a `Either` type.
 *
 * @category combinators
 * @since 2.3.0
 */
export const left = 
/*#__PURE__*/
compose(/*#__PURE__*/ _.prismLeft());
/**
 * Return a `Traversal` from a `Prism` focused on a `Traversable`.
 *
 * @category combinators
 * @since 2.3.0
 */
export function traverse(T) {
    return flow(asTraversal, _.traversalTraverse(T));
}
export function findFirst(predicate) {
    return composeOptional(_.optionalFindFirst(predicate));
}
export function findFirstNonEmpty(predicate) {
    return composeOptional(_.optionalFindFirstNonEmpty(predicate));
}
// -------------------------------------------------------------------------------------
// pipeables
// -------------------------------------------------------------------------------------
/**
 * @category Invariant
 * @since 2.3.0
 */
export const imap = (f, g) => (ea) => imap_(ea, f, g);
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
const imap_ = (ea, ab, ba) => prism(flow(ea.getOption, O.map(ab)), flow(ba, ea.reverseGet));
/**
 * @category instances
 * @since 2.3.0
 */
export const URI = 'monocle-ts/Prism';
/**
 * @category instances
 * @since 2.3.0
 */
export const Invariant = {
    URI,
    imap: imap_
};
/**
 * @category instances
 * @since 2.3.8
 */
export const Semigroupoid = {
    URI,
    compose: (ab, ea) => compose(ab)(ea)
};
/**
 * @category instances
 * @since 2.3.0
 */
export const Category = {
    URI,
    compose: Semigroupoid.compose,
    id
};
